import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { serviceLoading } from "src/common/apiLoader/state/selection";
import { ServiceContract } from "src/common/models/contract";
import { SearchableTable } from "src/components/SearchableTable";
import { cleanCurrentContract, getServiceContractByIdApi, getServiceContractsApi } from "src/features/ServiceContract/state/action";
import { GET_CONTRACT_BY_ID_API, GET_SERVICE_CONTRACTS_API } from "src/features/ServiceContract/state/actionTypes";
import { RootState } from "src/state/reducer";
import { ColumnsKeys, summaryColumns, TABLE_COLUMNS } from "./config";
import { Button, Select, Space, Tooltip } from "antd";
import { ProfileOutlined } from '@ant-design/icons'
import { SystemDescriptions } from "src/common/descriptions";
import { searchableTableUtils } from "src/components/SearchableTable/utils";
import { Loader } from "src/components/Loader";
import { ContractConfirmation } from "src/features/ServiceContract/CreateServiceContract/ContractConfirmation";

enum FilterOptions {
    ALL = "ALL",
    ACTIVE = "ACTIVE",
    INACTIVE = "INACTIVE",
}

interface ReduxProps {
    serviceContracts: ServiceContract[]
    isGettingContracts: boolean
    isGettingContract: boolean
    currentContract?: Partial<ServiceContract>
}

export const CustomerContracts = () => {
    const dispatch = useDispatch()
    const { id: customerId } = useParams<{ id: string }>();

    const [selectedFilter, setSelectedFiler] = useState<FilterOptions>(FilterOptions.ACTIVE)
    const [displayContracts, setDisplayContracts] = useState<ServiceContract[]>([])

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        serviceContracts: state.serviceContract.serviceContracts || [],
        currentContract: state.serviceContract.currentContract,
        isGettingContracts: serviceLoading(state, [GET_SERVICE_CONTRACTS_API]),
        isGettingContract: serviceLoading(state, [GET_CONTRACT_BY_ID_API]),
    }))

    const descriptions = SystemDescriptions.PAGES.SERVICE_CONTRACT.SERVICE_CONTRACTS
    const commonDescriptions = SystemDescriptions.PAGES.COMMON

    useEffect(() => {
        if (customerId) {
            dispatch(getServiceContractsApi({
                customerId: Number(customerId),
            }))
        }
    }, [])

    useEffect(() => {
        if (!reduxProps.serviceContracts) {
            return;
        }

        if (selectedFilter === FilterOptions.ALL) {
            setDisplayContracts(reduxProps.serviceContracts)
            return;
        } else if (selectedFilter === FilterOptions.ACTIVE) {
            setDisplayContracts(reduxProps.serviceContracts.filter(c => c.isActive))
            return;
        } else if (selectedFilter === FilterOptions.INACTIVE) {
            setDisplayContracts(reduxProps.serviceContracts.filter(c => !c.isActive))
            return;
        }
    }, [reduxProps.serviceContracts, selectedFilter])


    const tblColumns: ColumnsType<ServiceContract> = [
        ...TABLE_COLUMNS,
        {
            title: descriptions.TABLE_COLUMNS.ACTIONS,
            dataIndex: ColumnsKeys.ACTIONS,
            key: ColumnsKeys.ACTIONS,
            align: searchableTableUtils.alignment.centerAlign,
            render: (_, record: ServiceContract) => {
                const handleViewClick = (contractId: number) => {
                    dispatch(getServiceContractByIdApi(contractId))
                }

                return <Space size="middle">
                    <Tooltip title={commonDescriptions.ACTIONS.VIEW} >
                        <Button type="primary" ghost shape="circle" size="small"
                            icon={<ProfileOutlined />}
                            onClick={() => handleViewClick(record.id!)}
                        />
                    </Tooltip>
                </Space>
            },
        },
    ]

    const renderLoader = () => (
        <Loader
            isVisible={reduxProps.isGettingContract}
            title={descriptions.MAIN_ACTION_TITLE}
            description={descriptions.MAIN_ACTION_DESCRIPTION}
        />
    )

    const closeModal = () => {
        dispatch(cleanCurrentContract())
    }

    const handleFilterChange = (e) => {
        setSelectedFiler(e)
    }

    const renderFilterSection = () => {
        return (
            <div style={{ width: '100%', display: 'flex', alignItems: 'end', justifyContent: 'end' }}>
                <Select
                    style={{ width: 250 }}
                    defaultValue={"ACTIVE"}
                    options={[
                        {
                            value: FilterOptions.ALL,
                            label: "Todos los contratos",
                        },
                        {
                            value: FilterOptions.ACTIVE,
                            label: "Contratos Activos",
                        },
                        {
                            value: FilterOptions.INACTIVE,
                            label: "Contratos Inactivos",
                        },
                    ]}
                    placeholder={"Selecciona una vista"}
                    onChange={handleFilterChange}
                />
            </div>
        )
    }

    return (
        <>
            <SearchableTable
                showSearch
                showLoader={reduxProps.isGettingContracts}
                items={displayContracts}
                tableColumns={tblColumns}
                searchLabel={descriptions.TABLE.SEARCH_PLACEHOLDER}
                summaryColumns={summaryColumns}
            >
                {renderFilterSection()}
            </SearchableTable>
            {
                reduxProps.currentContract &&
                <ContractConfirmation
                    open={!!reduxProps.currentContract}
                    onCancel={() => { closeModal() }}
                    onAccept={() => { closeModal() }}
                    okText={descriptions.ACCEPT}
                    cancelText={descriptions.BACK}
                    currentContract={reduxProps.currentContract}
                    readOnly
                    type="SERVICE_CONTRACT"
                />
            }
            {renderLoader()}
        </>
    )
}
